import { isImageGraphic, type SymbolGraphic } from './model';
import { resolveSymbolImage, symbolSampleBounds } from './scene';
import { EMPTY_SYMBOL_CATALOGUE, type PreparedSymbolCatalogue } from './images';
import type { SymbolPresentation } from './backing';

/** One temporary Canvas per distinct prepared variant, never per feature or legend row. */
export class SymbolCanvasPainter {
  #canvases = new Map<string, HTMLCanvasElement>();
  constructor(readonly images: PreparedSymbolCatalogue = EMPTY_SYMBOL_CATALOGUE) {}
  draw(
    ctx: CanvasRenderingContext2D,
    graphic: SymbolGraphic,
    colour: string,
    tint: string | null | undefined,
    x: number,
    y: number,
    longEdge: number,
    rotation = 0,
    opacity = 1,
    presentation?: SymbolPresentation,
  ): boolean {
    if (!isImageGraphic(graphic)) return false;
    const image = resolveSymbolImage(this.images, graphic, colour, tint, presentation);
    let canvas = this.#canvases.get(image.id);
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not prepare the symbol for the page.');
      const pixels = context.createImageData(image.width, image.height);
      pixels.data.set(image.data);
      context.putImageData(pixels, 0, 0);
      this.#canvases.set(image.id, canvas);
    }
    const size = symbolSampleBounds(image, longEdge, rotation);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.globalAlpha = image.composition ? 1 : opacity;
    ctx.drawImage(canvas, -size.width / 2, -size.height / 2, size.width, size.height);
    ctx.restore();
    return true;
  }
  clear() {
    for (const canvas of this.#canvases.values()) {
      canvas.width = 0;
      canvas.height = 0;
    }
    this.#canvases.clear();
  }
}
